import React, { useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Modal,
  SkeletonLoader,
  Space,
  TextBody,
  TextHeading,
  Box,
  useFocus,
  Link,
  useMediaSize,
} from '@ally/metronome-ui'
import styled from 'styled-components'
import { TransactionHistoryTableModel, CheckDetails } from '../../../../types'
import {
  amountFormatter,
  getServerTimeShortDate,
  getServerTime,
} from '../../../../utils/formatter'
import { CheckImages } from './common/CheckImages'
import { useTransactionDetails } from './common/hooks/useTransactionDetails'
import { HOLD_DESCRIPTION } from '../../../../utils/constants'
import { EddModalContent } from '../../../Checking/EarlyDirectDeposit'
import { TransactionDetailsRows } from '../TransactionDetailsRows'
import { DisputeLink } from './common/DisputeLink'

interface TransactionDetailModalProps {
  data: TransactionHistoryTableModel | undefined | JSX.Element
  printCheckCallback: (data: CheckDetails) => void
  showModal: boolean
  closeModal: () => void
}

interface TransactionDetailModalContentProps {
  data: TransactionHistoryTableModel | JSX.Element
  printCheckCallback: (data: CheckDetails) => void
}

const StyledButtonDisplayBlock = styled(Button)`
  display: block;
`

export const getDisputeStatus = (disputeStatus: string): string => {
  let statusText = ''
  const isPending = /\b(\w*Pending\w*)\b/
  switch (disputeStatus) {
    case 'New':
      statusText = 'Ready for review'
      break
    case isPending.test(disputeStatus) && disputeStatus:
      /* Switch cases use strict comparison and `isPending.test(disputeStatus)` returns a boolean. The
        addition of `&& disputeStatus` is used to pass only a matching string value of `disputeStatus`. */
      statusText = 'In progress'
      break
    case 'Closed':
      statusText = 'Closed'
      break
    // this can be tested by created a test where dispute status does not match the cases listed
    /* istanbul ignore next */
    default:
      break
  }
  return statusText
}

export const TransactionDetailModalContent: React.FC<TransactionDetailModalContentProps> =
  ({ data, printCheckCallback }) => {
    const {
      detailsLoading,
      detailsError,
      transactionDetail,
      getTransactionDetails,
    } = useTransactionDetails()
    const isMdDown = useMediaSize('MdDown')
    /*
     * For Deposited Checks, we use the data passed in via Transaction History API call
     * For all other transactions, make a transaction detail call using `useTransactionDetails`
     */
    // Data from transaction details call
    const {
      checkTransactionIndicator,
      transactionCheckNumberPvtEncrypt,
      checkImageUrl,
      eligibilityStatus,
      transactionDescription,
      transactionAmountPvtEncrypt,
      transactionPostingDate,
      disputeStatus,
      checkHold,
      transactionId,
      transactionSequenceNumber,
    } = transactionDetail

    // Passed in data from transaction history api
    const {
      id,
      amount,
      description,
      transactionEffectiveDate,
      showEdd,
      isDepositedCheck,
    } = data as TransactionHistoryTableModel

    const [headerRef, setHeaderFocus] = useFocus()
    const displayDetails = !detailsLoading && !detailsError
    const displayCheckImages = checkTransactionIndicator && checkImageUrl
    const [eddContent, setEddContent] = useState<boolean>(false)

    const displayCheckNumber =
      checkTransactionIndicator && transactionCheckNumberPvtEncrypt
    const displayDisputeLink =
      eligibilityStatus === 'Eligible' && !disputeStatus

    /* istanbul ignore next */
    const focusHandler = (): void => {
      if (!detailsLoading || !detailsError) {
        setHeaderFocus()
      }
    }

    useEffect(() => {
      // If transaction is a deposited check we don't make the details call
      if (!isDepositedCheck) {
        getTransactionDetails(id).then(() => focusHandler())
      }
    }, [])

    if (eddContent) {
      return <EddModalContent />
    }

    const renderDepositedCheckDetailModal = (): React.ReactElement => {
      const transactionDetailDataDepositedCheck = [
        {
          heading: 'Description:',
          content: description,
        },
        {
          heading: 'Amount:',
          content: amount,
          isAmount: true,
        },
        {
          heading: 'Transaction Date:',
          content:
            transactionEffectiveDate &&
            getServerTimeShortDate(transactionEffectiveDate),
        },
        {
          heading: 'Type:',
          content: 'Deposit',
        },
        {
          heading: (
            <TextBody size="sm" tag="div">
              We&apos;re processing your deposit, which could take up to 1
              business day. We&apos;ll send you a notification with details on
              the status of your deposit and the availability of your funds.
            </TextBody>
          ),
        },
      ]

      return (
        <Box as="section">
          <Space pb="sm">
            <TextHeading
              size="md"
              tabIndex={-1}
              ref={headerRef}
              data-testid="transaction-detail-modal-title"
            >
              Transaction Details
            </TextHeading>
            <TransactionDetailsRows
              data={transactionDetailDataDepositedCheck}
            />
          </Space>
        </Box>
      )
    }

    const renderTransactionDetailModal = (): React.ReactElement => {
      const displayDisputeMessage =
        disputeStatus === 'Closed' ||
        (eligibilityStatus === 'EligibleViewOnly' && !disputeStatus)

      const transactionDetailData = [
        {
          heading: 'Check Number:',
          content: transactionCheckNumberPvtEncrypt,
          hidden: !displayCheckNumber,
        },
        {
          heading: 'Description:',
          content: transactionDescription,
          isPrivate: true,
        },
        {
          heading: (
            <Space mt="-10px" mb="sm">
              <TextBody tag="div" size="sm" weight="normal">
                Early Direct Deposit
                <span aria-hidden="true">💰</span>
              </TextBody>
            </Space>
          ),
          hidden: !showEdd,
          isPrivate: true,
        },
        {
          heading: 'Amount:',
          content: transactionDetail.transactionAmountPvtEncrypt,
          isAmount: true,
        },
        {
          heading: 'Posting Date:',
          content: getServerTimeShortDate(transactionPostingDate),
        },
        {
          heading: 'Posting Time:',
          content: getServerTime(transactionPostingDate),
        },
        {
          heading: 'Type:',
          content: transactionAmountPvtEncrypt < 0 ? 'Withdrawal' : 'Deposit',
        },
        {
          heading: 'Dispute Status:',
          content: disputeStatus && getDisputeStatus(disputeStatus),
          hidden: !disputeStatus,
        },
        {
          heading: (
            <TextBody size="sm" tag="div">
              You got paid early.
              <StyledButtonDisplayBlock
                data-testid="mobile-learn-more-edd"
                text="Learn more about Early Direct Deposit"
                variant="link"
                onClick={(): void => setEddContent(true)}
              />
            </TextBody>
          ),
          hidden: !showEdd,
        },
        {
          heading: (
            <Space mb="sm">
              <TextBody size="sm" tag="div">
                If you would like to dispute this transaction, call us at{' '}
                <Link text="1-877-247-2559" to="tel:18772472559" /> so we can
                take further action to protect your account.
              </TextBody>
            </Space>
          ),

          hidden: !displayDisputeMessage,
        },
        {
          heading: (
            <Box mb="md">
              <DisputeLink
                transactionId={transactionId}
                transactionSequenceNumber={transactionSequenceNumber}
              />
            </Box>
          ),
          hidden: !displayDisputeLink || isMdDown,
        },

        {
          heading: 'Funds Availability:',
          content: (
            <>
              {checkHold?.fundsAvailability.map((fund, i) => {
                if (fund.businessDay === 1) {
                  return (
                    /* eslint-disable react/no-array-index-key */
                    <TextBody size="sm" tag="div" key={i}>
                      {`Next business day: ${amountFormatter.format(
                        fund.amountAvailablePvtEncrypt,
                      )}`}
                    </TextBody>
                  )
                }
                return (
                  <TextBody size="sm" tag="div" key={i}>
                    {`${
                      fund.businessDay
                    } business days: ${amountFormatter.format(
                      fund.amountAvailablePvtEncrypt,
                    )}`}
                  </TextBody>
                )
              })}
            </>
          ),
          hidden: !checkHold,
        },
        {
          heading: checkHold ? (
            <TextBody size="sm" tag="div">
              {HOLD_DESCRIPTION[checkHold.holdCode]}
            </TextBody>
          ) : (
            ''
          ),
          hidden: !checkHold,
        },
        {
          heading: (
            <TextBody size="sm" tag="div">
              <CheckImages
                transactionDetail={transactionDetail}
                printCheckCallback={printCheckCallback}
              />
            </TextBody>
          ),
          hidden: !displayCheckImages,
        },
      ]

      return (
        <>
          {detailsError && (
            <Alert
              disableContainer
              variant="error"
              heading="This information isn't available right now. Try again later."
            />
          )}
          {detailsLoading && (
            <SkeletonLoader.Container>
              <SkeletonLoader.Row height="l" width="l" />
              <SkeletonLoader.Row height="m" width="s" />
              <SkeletonLoader.Row height="m" width="m" />
              <SkeletonLoader.Row height="m" width="s" />
              <SkeletonLoader.Row height="m" width="m" />
              <SkeletonLoader.Row height="m" width="s" />
              <SkeletonLoader.Row height="m" width="s" />
              <>
                {displayCheckImages && (
                  <SkeletonLoader.Row height="xl" width="m" />
                )}
              </>
            </SkeletonLoader.Container>
          )}
          {displayDetails && (
            <Box as="section">
              <Space pb="sm">
                <TextHeading
                  size="md"
                  tabIndex={-1}
                  ref={headerRef}
                  data-testid="transaction-detail-modal-title"
                >
                  Transaction Details
                </TextHeading>
                <TransactionDetailsRows data={transactionDetailData} />
              </Space>
            </Box>
          )}
        </>
      )
    }

    return (
      <Box data-testid="transaction-detail-modal">
        {isDepositedCheck
          ? renderDepositedCheckDetailModal()
          : renderTransactionDetailModal()}
      </Box>
    )
  }

export const TransactionDetailModal: React.FC<TransactionDetailModalProps> = ({
  data,
  printCheckCallback,
  showModal,
  closeModal,
}) => {
  if (!data) return <></>
  const { isDepositedCheck, showEdd } = data as TransactionHistoryTableModel

  return (
    <>
      <Modal
        content={
          <TransactionDetailModalContent
            data={data}
            printCheckCallback={printCheckCallback}
          />
        }
        fullScreenMd
        manualToggle
        setShowOption={showModal}
        onDismissCallback={closeModal}
        disableDismiss
      />
      {isDepositedCheck && (
        <TextBody tag="div" size="sm" weight="normal">
          Deposit pending approval
        </TextBody>
      )}
      {showEdd && (
        <TextBody tag="div" size="sm" weight="normal">
          Early Direct Deposit
          <span aria-hidden="true">💰</span>
        </TextBody>
      )}
    </>
  )
}
